import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ReminderChannel } from '../enums/reminder-channel.enum';
import { CvData } from '../interfaces/cv-data';
import { Job } from '../interfaces/job';
import { JobDetail } from '../interfaces/job-detail';
import { JobboardFilter } from '../interfaces/jobboard-filter';

@Injectable({
  providedIn: 'root',
})
export class JobboardAdapter {
  private http = inject(HttpClient);

  getJobs(
    options: GetJobsOptions
  ): Observable<{ results: Array<Job>; total: number; filterTotal: number }> {
    const paramsObject = {
      offset: options?.offset ?? 0,
      limit: options?.limit ?? -1,
    };
    if (options?.search) {
      paramsObject['search'] = options?.search;
    }
    if (options?.employmentTypes) {
      paramsObject['employmentTypes[]'] = options?.employmentTypes;
    }
    if (options?.workingTimes) {
      paramsObject['workingTimes[]'] = options?.workingTimes;
    }
    if (options?.facilities) {
      paramsObject['facilities[]'] = options?.facilities;
    }
    if (options?.companyIds) {
      paramsObject['companyIds[]'] = options.companyIds;
    }

    return this.http.get<{
      results: Array<Job>;
      total: number;
      filterTotal: number;
    }>(`${environment.apiBaseUrl}/jobboard/jobs`, {
      params: new HttpParams({
        fromObject: paramsObject,
      }),
    });
  }

  getCompanyJobs(
    id: number,
    options: GetJobsOptions
  ): Observable<{ results: Array<Job>; total: number; filterTotal: number }> {
    const paramsObject = {
      offset: options?.offset ?? 0,
      limit: options?.limit ?? -1,
    };
    if (options?.search) {
      paramsObject['search'] = options?.search;
    }
    if (options?.employmentTypes) {
      paramsObject['employmentTypes[]'] = options?.employmentTypes;
    }
    if (options?.workingTimes) {
      paramsObject['workingTimes[]'] = options?.workingTimes;
    }
    if (options?.facilities) {
      paramsObject['facilities[]'] = options?.facilities;
    }

    return this.http.get<{
      results: Array<Job>;
      total: number;
      filterTotal: number;
    }>(`${environment.apiBaseUrl}/jobboard/companies/${id}/jobs`, {
      params: new HttpParams({
        fromObject: paramsObject,
      }),
    });
  }

  getJob(jobId: number): Observable<JobDetail> {
    return this.http.get<JobDetail>(
      `${environment.apiBaseUrl}/jobboard/jobs/${jobId}`
    );
  }

  getCompanies(offset: number = 0, limit: number = -1): Observable<any> {
    const params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    return this.http.get(`${environment.apiBaseUrl}/jobboard/companies`, {
      params,
    });
  }

  applyToJob(jobId: number, payload: FormData): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/jobs/${jobId}/apply`,
      payload
    );
  }

  applyInitiative(jobId: number, payload: FormData): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/companies/${jobId}/apply`,
      payload
    );
  }

  getMissingData(applicationId: number, nonce: string) {
    const params = new HttpParams().set('nonce', nonce);

    return this.http.get(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/missing-data`,
      { params }
    );
  }

  setMissingData(applicationId: number, payload) {
    return this.http.patch(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/missing-data`,
      payload
    );
  }

  trackMissingDataClick(applicationId: number, utm_source?: string) {
    const payload = utm_source ? { utm_source } : undefined;
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/track-click`,
      payload
    );
  }

  getDesign(companyId: number): Observable<any> {
    return this.http.get(
      `${environment.apiBaseUrl}/general/companies/${companyId}/design`
    );
  }

  getLogo(companyId: number): Observable<any> {
    return this.http.get(
      `${environment.apiBaseUrl}/general/companies/${companyId}/logo`,
      { responseType: 'blob' }
    );
  }

  getIcon(companyId: number): Observable<any> {
    return this.http.get(
      `${environment.apiBaseUrl}/general/companies/${companyId}/icon`,
      { responseType: 'blob' }
    );
  }

  getCompanyInfo(companyId: number): Observable<any> {
    return this.http.get(
      `${environment.apiBaseUrl}/general/companies/${companyId}`
    );
  }

  acceptTalentpoolInvitation(applicationId: string, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/accept-talentpool-invitation`,
      { nonce }
    );
  }

  declineTalentpoolInvitation(applicationId: string, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/decline-talentpool-invitation`,
      { nonce }
    );
  }

  countJobClick(companyId: number, jobId: number, payload: any) {
    return this.http
      .post(
        `${environment.apiBaseUrl}/jobboard/companies/${companyId}/jobs/${jobId}/click`,
        payload
      )
      .pipe(shareReplay());
  }

  unsubscribeReminder(applicationId: number, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/disable-reminder`,
      { nonce }
    );
  }

  updateReminderChannels(
    applicationId: number,
    nonce: string,
    channels: ReminderChannel[]
  ) {
    return this.http.patch(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/reminder-channels`,
      {
        nonce,
        channels,
      }
    );
  }

  getReminderChannels(applicationId: number, nonce: string) {
    return this.http.get(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/reminder-channels?nonce=${nonce}`
    );
  }

  verifyApplicantEmail(nonce: string): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/applicant/auth/email-verification`,
      { nonce }
    );
  }

  getFilterOptions(): Observable<JobboardFilter> {
    return this.http.get<JobboardFilter>(
      `${environment.apiBaseUrl}/jobboard/filter-options`
    );
  }

  getCompanyFilterOptions(companyId: number): Observable<JobboardFilter> {
    return this.http.get<JobboardFilter>(
      `${environment.apiBaseUrl}/jobboard/companies/${companyId}/filter-options`
    );
  }

  getPrefillDataFromCv(cv: FormData): Observable<CvData> {
    return this.http.post<CvData>(
      `${environment.apiBaseUrl}/jobboard/applications/extract-cv`,
      cv
    ); //TODO url and payload
  }

  generateStandaloneISA(payload: StandaloneISARequest): Observable<void> {
    return this.http.post<void>(
      `${environment.apiBaseUrl}/jobboard/standalone-isa`,
      payload
    );
  }

  getStandaloneISAInfo(id: string): Observable<string> {
    return this.http.get<string>(
      `${environment.apiBaseUrl}/jobboard/standalone-isa/${id}`
    );
  }

  countStandaloneISAClick(id: string) {
    return this.http.post<void>(
      `${environment.apiBaseUrl}/jobboard/standalone-isa/${id}/click`,
      null
    );
  }

  countStandaloneISABooking(id: string) {
    return this.http.post<void>(
      `${environment.apiBaseUrl}/jobboard/standalone-isa/${id}/booking`,
      null
    );
  }

  getPrefillData(nonce: string, jobId: number) {
    return this.http.get<{ valid: boolean }>(
      `${environment.apiBaseUrl}/jobboard/jobs/prefill-data/${nonce}`,
      { params: { jobId } }
    );
  }

  applyWithPrefillData(jobId: number, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/jobs/${jobId}/apply-prefilled`,
      { nonce }
    );
  }

  unsubscribeTalentpool(payload: { email: string; captchaCode: string }) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/unsubscribe-talentpool`,
      payload
    );
  }

  startPhoneCall(nonce: string) {
    return this.http.post(`${environment.apiBaseUrl}/phone-calls/start-call`, {
      nonce,
    });
  }

  noInterestInJob(jobId: number, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/jobs/${jobId}/no-interest`,
      { nonce }
    );
  }

  unsubscribeKiraMailChat(applicationId: number, nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/${applicationId}/unsubscribe-kira-mail-chat`,
      { nonce }
    );
  }

  renewTermsConsent(nonce: string) {
    return this.http.post(
      `${environment.apiBaseUrl}/jobboard/applications/renew-terms-consent`,
      { nonce },
    );
  }
}

export type GetJobsOptions = {
  limit?: number;
  offset?: number;
  facilities?: string[];
  employmentTypes?: number[];
  workingTimes?: number[];
  companyIds?: number[];
  search?: string;
};
export type StandaloneISARequest = {
  firstName: string;
  lastName: string;
  email: string;
  isa: number[];
  language: string;
  isaLinkId?: string;
  allowRetargeting: boolean;
};
